import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { Card } from '../../../components/ui/Card';
import AccountSwitcher from '../../../components/AccountSwitcher';
import { useAccounts } from '../../../context/AccountContext';
import { RefreshCw, Search, ChevronDown, ChevronRight, Users, Target, Copy, Sparkles, Globe, MapPin, Briefcase, GraduationCap, Heart, Loader2, AlertTriangle } from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';
const DUMMY_DATA_BRAND_ID = 'ec9873c2-0edb-44eb-ac6f-2670c8e56dc4';

const generateDummyAudiences = () => ([
  { id: '23847563891001', name: 'LAL - Purchasers 1%', subtype: 'LOOKALIKE', approximate_count: 850000, delivery_status: 'ready', operation_status: 'Normal', time_created: '2024-08-15', time_updated: '2024-12-20' },
  { id: '23847563891002', name: 'LAL - Purchasers 2%', subtype: 'LOOKALIKE', approximate_count: 1700000, delivery_status: 'ready', operation_status: 'Normal', time_created: '2024-08-15', time_updated: '2024-12-20' },
  { id: '23847563892001', name: 'Website - All Visitors 180D', subtype: 'CUSTOM', approximate_count: 2500000, delivery_status: 'ready', operation_status: 'Normal', time_created: '2024-06-01', time_updated: '2024-12-25' },
  { id: '23847563892002', name: 'Website - Purchasers 30D', subtype: 'CUSTOM', approximate_count: 45000, delivery_status: 'ready', operation_status: 'Normal', time_created: '2024-06-01', time_updated: '2024-12-25' },
  { id: '23847563893001', name: 'CRM - All Customers', subtype: 'CUSTOM', approximate_count: 890000, delivery_status: 'ready', operation_status: 'Normal', time_created: '2024-05-01', time_updated: '2024-12-20' },
  { id: '23847563895001', name: 'INT - Fashion & Apparel TR', subtype: 'SAVED', approximate_count: 12500000, delivery_status: 'ready', operation_status: 'Normal', time_created: '2024-04-01', time_updated: '2024-11-15' },
  { id: '23847563895002', name: 'INT - Online Shoppers TR', subtype: 'SAVED', approximate_count: 18000000, delivery_status: 'ready', operation_status: 'Normal', time_created: '2024-04-01', time_updated: '2024-11-15' },
  { id: '23847563897001', name: 'IG - Profile Engagers 90D', subtype: 'CUSTOM', approximate_count: 520000, delivery_status: 'ready', operation_status: 'Normal', time_created: '2024-08-01', time_updated: '2024-12-23' }
]);

const generateDummyDetail = (audience) => ({
  id: audience.id,
  name: audience.name,
  approximate_count: audience.approximate_count,
  delivery_status: audience.delivery_status,
  operation_status: audience.operation_status,
  time_created: audience.time_created,
  time_updated: audience.time_updated,
  description: `${audience.name} - Auto-generated audience for targeting`,
  subtype: audience.subtype
});

export default function MetaAudiences() {
  const { selectedBrand, user } = useAuth();
  const { getSelectedAccountId } = useAccounts();
  const selectedAccountId = getSelectedAccountId('META_ADS');
  const [audiences, setAudiences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [expandedCategories, setExpandedCategories] = useState({});

  // Per-audience performance (spend/revenue/ROAS/CPA) — computed server-side
  // from adset.targeting + insights, prorated equally across each adset's
  // targeted audiences.
  const [perfData, setPerfData] = useState({});  // { [audienceId]: { spend, revenue, roas, cpa, purchases, attribution, adsetCount } }
  const [perfLoading, setPerfLoading] = useState(false);
  const [perfRange, setPerfRange] = useState('last30');
  const [perfStats, setPerfStats] = useState(null);

  // Targeting Search states
  const [targetingSearch, setTargetingSearch] = useState('');
  const [targetingType, setTargetingType] = useState('adinterest');
  const [targetingResults, setTargetingResults] = useState([]);
  const [targetingLoading, setTargetingLoading] = useState(false);
  const [targetingError, setTargetingError] = useState('');
  const [showTargetingSearch, setShowTargetingSearch] = useState(false);

  const targetingTypes = [
    { value: 'adinterest', label: 'Interests', icon: Heart },
    { value: 'adgeolocation', label: 'Locations', icon: MapPin },
    { value: 'adlocale', label: 'Languages', icon: Globe },
    { value: 'adworkposition', label: 'Job Titles', icon: Briefcase },
    { value: 'adeducationschool', label: 'Schools', icon: GraduationCap },
    { value: 'adworkemployer', label: 'Employers', icon: Briefcase },
  ];

  const searchTargeting = async () => {
    if (!targetingSearch.trim() && targetingType !== 'adlocale') return;
    if (!selectedBrand?.id) return;

    setTargetingLoading(true);
    setTargetingError('');
    setTargetingResults([]);

    try {
      const params = new URLSearchParams({
        q: targetingSearch,
        type: targetingType,
        limit: '50'
      });
      
      if (targetingType === 'adgeolocation') {
        params.set('location_types', 'country,region,city');
      }

      const res = await fetch(`${API_BASE}/api/meta-ads/targeting-search?${params}`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': selectedBrand.id }
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || json.details || 'Search failed');
      setTargetingResults(json.data || []);
    } catch (err) {
      setTargetingError(err.message);
    } finally {
      setTargetingLoading(false);
    }
  };

  const toggleCategory = (key) => {
    setExpandedCategories(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getCategoryIcon = (type) => {
    switch (type) {
      case 'LOOKALIKE': return <Copy className="w-4 h-4" />;
      case 'CUSTOM': return <Target className="w-4 h-4" />;
      case 'SAVED': return <Sparkles className="w-4 h-4" />;
      default: return <Users className="w-4 h-4" />;
    }
  };

  const getCategoryColor = (type) => {
    switch (type) {
      case 'LOOKALIKE': return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'CUSTOM': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'SAVED': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getCategoryBg = (type) => {
    switch (type) {
      case 'LOOKALIKE': return 'bg-purple-50/50 border-purple-100';
      case 'CUSTOM': return 'bg-blue-50/50 border-blue-100';
      case 'SAVED': return 'bg-emerald-50/50 border-emerald-100';
      default: return 'bg-gray-50/50 border-gray-100';
    }
  };

  const formatCount = (count) => {
    if (count == null) return '—';
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(0)}K`;
    return count.toLocaleString();
  };

  const currency = selectedBrand?.currency || 'TRY';
  const formatMoneyShort = (n) => {
    const v = Number(n || 0);
    if (!Number.isFinite(v) || v === 0) return '—';
    const sym = currency === 'TRY' ? '₺' : currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency + ' ';
    if (v >= 1_000_000) return `${sym}${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `${sym}${(v / 1_000).toFixed(1)}K`;
    return `${sym}${Math.round(v).toLocaleString('en-US')}`;
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return audiences;
    const q = search.toLowerCase();
    return audiences.filter(a =>
      (a.name || '').toLowerCase().includes(q) ||
      (a.subtype || '').toLowerCase().includes(q) ||
      (a.id || '').toLowerCase().includes(q)
    );
  }, [audiences, search]);

  const grouped = useMemo(() => {
    const groups = new Map();
    for (const a of filtered) {
      const k = a.subtype || 'UNKNOWN';
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push(a);
    }
    const order = Array.from(groups.keys()).sort((a, b) => a.localeCompare(b));
    return order.map(key => ({ key, items: groups.get(key) }));
  }, [filtered]);

  const loadAudiences = async () => {
    if (!selectedBrand?.id) return;
    setLoading(true);
    setError('');
    setAudiences([]);
    try {
      if (selectedBrand.id === DUMMY_DATA_BRAND_ID) {
        setAudiences(generateDummyAudiences());
        return;
      }
      const connResp = await fetch(`${API_BASE}/api/auth/connections`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': selectedBrand.id }
      });
      if (!connResp.ok) {
        const d = await connResp.json().catch(() => ({}));
        throw new Error(d?.error || d?.details || `HTTP ${connResp.status}`);
      }
      const connData = await connResp.json();
      const connections = Array.isArray(connData) ? connData : (Array.isArray(connData?.connections) ? connData.connections : []);
      const metaAds = connections.find(c => c.platform === 'META_ADS' && c.status === 'ACTIVE');
      const adAccountId = metaAds?.customerId || null;
      const url = new URL(`${API_BASE}/api/meta-ads/audiences`);
      if (adAccountId) url.searchParams.set('adAccountId', adAccountId);
      const resp = await fetch(url.toString(), {
        credentials: 'include',
        headers: { 'X-Brand-ID': selectedBrand.id }
      });
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        throw new Error(d?.error || d?.details || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      setAudiences(Array.isArray(data?.data) ? data.data : []);
    } catch (e) {
      setError(e?.message || 'Failed to load Meta audiences');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAudiences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand?.id, reloadKey, selectedAccountId]);

  const loadPerformance = async () => {
    if (!selectedBrand?.id) { setPerfData({}); setPerfStats(null); return; }
    if (selectedBrand.id === DUMMY_DATA_BRAND_ID) {
      // Demo fake performance keyed by dummy audience IDs
      const demo = {};
      const dummies = generateDummyAudiences();
      dummies.forEach((a, i) => {
        const factor = 1 - i * 0.08;
        demo[a.id] = {
          spend: Math.round(420000 * Math.max(factor, 0.1)),
          revenue: Math.round(1850000 * Math.max(factor, 0.1)),
          purchases: Math.round(280 * Math.max(factor, 0.1)),
          roas: 4.4,
          cpa: 1500,
          attribution: i % 3 === 0 ? 'exact' : 'estimated',
          adsetCount: 2 + i
        };
      });
      setPerfData(demo);
      setPerfStats({ totalAdsets: dummies.length * 2, audiencesCovered: dummies.length });
      return;
    }
    setPerfLoading(true);
    try {
      const url = new URL(`${API_BASE}/api/meta-ads/audiences/performance`);
      url.searchParams.set('dateRange', perfRange);
      const resp = await fetch(url.toString(), {
        credentials: 'include',
        headers: { 'X-Brand-ID': selectedBrand.id }
      });
      if (!resp.ok) { setPerfData({}); setPerfStats(null); return; }
      const data = await resp.json();
      setPerfData(data?.performance || {});
      setPerfStats(data?.stats || null);
    } catch (_) {
      setPerfData({}); setPerfStats(null);
    } finally {
      setPerfLoading(false);
    }
  };

  useEffect(() => {
    loadPerformance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand?.id, reloadKey, selectedAccountId, perfRange]);

  const openDetail = async (item) => {
    setActive(item);
    setDetail(null);
    setDetailError('');
    setDetailLoading(true);
    try {
      if (selectedBrand?.id === DUMMY_DATA_BRAND_ID) {
        setDetail(generateDummyDetail(item));
        return;
      }
      const url = new URL(`${API_BASE}/api/meta-ads/audiences/${encodeURIComponent(item.id)}`);
      const kind = (item.subtype || '') === 'SAVED' ? 'SAVED' : 'CUSTOM';
      url.searchParams.set('kind', kind);
      const resp = await fetch(url.toString(), {
        credentials: 'include',
        headers: { 'X-Brand-ID': selectedBrand.id }
      });
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        throw new Error(d?.error || d?.details || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      setDetail(data?.data || null);
    } catch (e) {
      setDetailError(e?.message || 'Failed to fetch audience detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setActive(null);
    setDetail(null);
    setDetailError('');
    setDetailLoading(false);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <Card className="rounded-2xl overflow-hidden shadow-sm border border-gray-100">
        <div className="p-6 bg-white text-gray-900 border-b flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center border border-blue-100">
              <img src="/6033716.png" alt="Meta" className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl tracking-tight">Meta Audiences</h1>
              <p className="text-sm text-gray-500">Custom and saved audiences from your Meta ad account — with revenue attribution per audience.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <AccountSwitcher platform="META_ADS" />
            <select
              value={perfRange}
              onChange={(e) => setPerfRange(e.target.value)}
              className="text-sm px-3 py-2 border border-gray-200 rounded-lg bg-white"
              title="Performance date range"
            >
              <option value="last7">Last 7 days</option>
              <option value="last30">Last 30 days</option>
              <option value="last90">Last 90 days</option>
              <option value="thisMonth">This month</option>
              <option value="lastMonth">Last month</option>
            </select>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                className="pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm"
                placeholder="Search audiences"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button
              type="button"
              onClick={() => setReloadKey((k) => k + 1)}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50"
              disabled={loading}
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading || perfLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <span className="px-3 py-1 rounded-xl text-xs bg-gray-100 text-gray-700">
              {loading ? 'Loading…' : `Loaded: ${audiences.length}`}
            </span>
            {perfStats && (
              <span className="px-3 py-1 rounded-xl text-xs bg-emerald-50 text-emerald-700 border border-emerald-100" title={`${perfStats.adsetsCoveredByAudience}/${perfStats.adsetsWithSpend} adsets matched to audiences`}>
                Attributed total: {formatMoneyShort(Object.values(perfData).reduce((s, p) => s + (p.revenue || 0), 0))}
              </span>
            )}
          </div>
        </div>
        {error && (
          <div className="px-6 py-4 bg-red-50 text-sm text-red-600">{error}</div>
        )}
      </Card>

      <div className="mt-6 space-y-3">
        {loading ? (
          <Card className="rounded-xl p-8 text-center">
            <RefreshCw className="w-6 h-6 text-gray-400 animate-spin mx-auto mb-2" />
            <div className="text-sm text-gray-500">Loading audiences...</div>
          </Card>
        ) : filtered.length === 0 ? (
          <Card className="rounded-xl p-8 text-center">
            <Users className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <div className="text-sm text-gray-500">No audiences found.</div>
          </Card>
        ) : (
          grouped.map(group => {
            const groupRevenue = group.items.reduce((s, a) => s + (perfData[a.id]?.revenue || 0), 0);
            const groupSpend = group.items.reduce((s, a) => s + (perfData[a.id]?.spend || 0), 0);
            const groupRoas = groupSpend > 0 ? (groupRevenue / groupSpend).toFixed(2) : null;
            return (
            <Card key={group.key} className={`rounded-xl overflow-hidden border ${getCategoryBg(group.key)}`}>
              {/* Category Header - Always Visible */}
              <button
                type="button"
                onClick={() => toggleCategory(group.key)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getCategoryColor(group.key)}`}>
                    {getCategoryIcon(group.key)}
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-gray-900">{group.key}</div>
                    <div className="text-xs text-gray-500">
                      {group.items.length} audience{group.items.length !== 1 ? 's' : ''}
                      {group.items.some(a => a.approximate_count != null) && (
                        <> · {formatCount(group.items.reduce((sum, a) => sum + (a.approximate_count || 0), 0))} total users</>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {groupRevenue > 0 && (
                    <>
                      <span className="px-2 py-1 rounded-lg text-[11px] font-medium bg-gray-100 text-gray-600" title="Total spend in this category for the selected period">
                        Spend: {formatMoneyShort(groupSpend)}
                      </span>
                      <span className="px-2 py-1 rounded-lg text-[11px] font-medium bg-emerald-100 text-emerald-700" title="Total attributed revenue">
                        Rev: {formatMoneyShort(groupRevenue)}
                      </span>
                      {groupRoas && (
                        <span className="px-2 py-1 rounded-lg text-[11px] font-medium bg-purple-100 text-purple-700">ROAS {groupRoas}x</span>
                      )}
                    </>
                  )}
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getCategoryColor(group.key)}`}>
                    {group.items.length}
                  </span>
                  {expandedCategories[group.key] ? (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </button>

              {/* Expanded Content */}
              {expandedCategories[group.key] && (
                <div className="px-4 pb-4 pt-1 bg-white/80">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {group.items.map(a => {
                      const p = perfData[a.id];
                      const isSaved = (a.subtype || '').toUpperCase() === 'SAVED';
                      return (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => openDetail(a)}
                        className="text-left border border-gray-200 rounded-xl p-3 bg-white hover:shadow-md hover:border-gray-300 transition-all w-full group"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900 truncate group-hover:text-blue-600 transition-colors" title={a.name || ''}>
                              {a.name || '—'}
                            </div>
                          </div>
                        </div>
                        <div className="mt-2 flex items-center justify-between">
                          {a.approximate_count != null ? (
                            <div className="flex items-center gap-1.5">
                              <Users className="w-3.5 h-3.5 text-gray-400" />
                              <span className="text-sm font-semibold text-gray-700">
                                {formatCount(a.approximate_count)}
                              </span>
                            </div>
                          ) : (
                            <div className="text-xs text-gray-400">Size not available</div>
                          )}
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                            a.delivery_status === 'ready' ? 'bg-green-50 text-green-700' :
                            a.delivery_status === 'pending' ? 'bg-yellow-50 text-yellow-700' :
                            a.delivery_status ? 'bg-gray-100 text-gray-600' : 'hidden'
                          }`}>
                            {a.delivery_status || ''}
                          </span>
                        </div>
                        {/* Performance metrics — pulled from /audiences/performance */}
                        {p && p.spend > 0 ? (
                          <div className="mt-2.5 pt-2.5 border-t border-gray-100">
                            <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-[11px]">
                              <div className="flex items-center justify-between">
                                <span className="text-gray-400">Spend</span>
                                <span className="font-medium text-gray-700 tabular-nums">{formatMoneyShort(p.spend)}</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-gray-400">Revenue</span>
                                <span className="font-medium text-emerald-700 tabular-nums">{formatMoneyShort(p.revenue)}</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-gray-400">ROAS</span>
                                <span className="font-medium text-purple-700 tabular-nums">{p.roas > 0 ? `${p.roas.toFixed(2)}x` : '—'}</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-gray-400">CPA</span>
                                <span className="font-medium text-orange-600 tabular-nums">{p.cpa > 0 ? formatMoneyShort(p.cpa) : '—'}</span>
                              </div>
                            </div>
                            <div className="mt-1 flex items-center justify-between text-[9px] text-gray-400">
                              <span>{p.adsetCount} adset{p.adsetCount !== 1 ? 's' : ''} · {Math.round(p.purchases)} purchase{p.purchases !== 1 ? 's' : ''}</span>
                              <span className={p.attribution === 'exact' ? 'text-emerald-600 font-medium' : 'text-amber-600'} title={p.attribution === 'exact' ? 'All adsets target only this audience — exact attribution' : 'Some adsets target multiple audiences — spend split equally'}>
                                {p.attribution === 'exact' ? 'exact' : 'estimated'}
                              </span>
                            </div>
                          </div>
                        ) : isSaved ? (
                          <div className="mt-2.5 pt-2.5 border-t border-gray-100 text-[10px] text-amber-700 bg-amber-50 -mx-3 -mb-3 px-3 py-2 rounded-b-xl flex items-start gap-1.5" title="Meta API does not return the saved_audience_id on adsets — when an adset is created from a saved audience, the targeting spec is copied but the audience ID reference is not retained, so revenue cannot be attributed back to the saved audience.">
                            <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                            <span>Attribution unavailable<br/><span className="text-amber-600">Meta API doesn't link adsets back to saved audiences</span></span>
                          </div>
                        ) : !perfLoading ? (
                          <div className="mt-2.5 pt-2.5 border-t border-gray-100 text-[11px] text-gray-400 italic">
                            No spend in selected period
                          </div>
                        ) : null}
                      </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </Card>
            );
          })
        )}
      </div>

      {active && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={closeDetail} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden border border-gray-200">
            {/* Header */}
            <div className="bg-gray-50 border-b border-gray-200 px-6 py-4">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0 pr-4">
                  <h2 className="text-lg font-semibold text-gray-900 truncate">
                    {active.name || 'Audience'}
                  </h2>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      active.subtype === 'LOOKALIKE' ? 'bg-purple-100 text-purple-700' :
                      active.subtype === 'SAVED' ? 'bg-indigo-100 text-indigo-700' :
                      active.subtype === 'ENGAGEMENT' ? 'bg-amber-100 text-amber-700' :
                      active.subtype === 'IG_BUSINESS' ? 'bg-pink-100 text-pink-700' :
                      active.subtype === 'CUSTOM' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {active.subtype || 'AUDIENCE'}
                    </span>
                    {detail?.time_created && (
                      <span className="text-xs text-gray-500">
                        Created {new Date(detail.time_created).toLocaleDateString()}
                      </span>
                    )}
                    {detail?.retention_days && (
                      <span className="text-xs text-gray-500">
                        · {detail.retention_days} days retention
                      </span>
                    )}
                  </div>
                </div>
                <button 
                  onClick={closeDetail} 
                  className="p-1.5 rounded-lg hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-5 max-h-[65vh] overflow-y-auto">
              {detailLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-300 border-t-gray-600"></div>
                  <span className="ml-3 text-gray-500 text-sm">Loading...</span>
                </div>
              ) : detailError ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{detailError}</div>
              ) : detail ? (
                <div className="space-y-4">
                  {/* Lookalike Spec */}
                  {detail.lookalike && (
                    <div className="rounded-xl bg-purple-50 border border-purple-100 p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Copy className="w-4 h-4 text-purple-600" />
                        <span className="text-sm font-semibold text-purple-900">Lookalike Configuration</span>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-white rounded-lg p-3 text-center border border-purple-100">
                          <div className="text-xs text-purple-600 mb-1">Ratio</div>
                          <div className="text-lg font-bold text-purple-900">{detail.lookalike.ratio || '—'}%</div>
                        </div>
                        <div className="bg-white rounded-lg p-3 text-center border border-purple-100">
                          <div className="text-xs text-purple-600 mb-1">Country</div>
                          <div className="text-lg font-bold text-purple-900">{detail.lookalike.country || '—'}</div>
                        </div>
                        <div className="bg-white rounded-lg p-3 text-center border border-purple-100">
                          <div className="text-xs text-purple-600 mb-1">Origin ID</div>
                          <div className="text-xs font-mono text-purple-900 truncate">{detail.lookalike.origin_audience_id || detail.lookalike.origin?.[0]?.id || '—'}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Rule for CUSTOM audiences */}
                  {detail.rule && (() => {
                    // Parse rule to extract readable info
                    const parseRule = (rule) => {
                      // Handle double-encoded JSON string
                      if (typeof rule === 'string') {
                        try { 
                          rule = JSON.parse(rule); 
                          // Check if still a string (double encoded)
                          if (typeof rule === 'string') {
                            rule = JSON.parse(rule);
                          }
                        } catch { return { items: [], ruleGroups: [] }; }
                      }
                      const items = [];
                      const ruleGroups = [];
                      
                      // Handle array of rules (video viewers, etc.)
                      if (Array.isArray(rule)) {
                        const events = rule.map(r => r.event_name).filter(Boolean);
                        const uniqueEvents = [...new Set(events)];
                        if (uniqueEvents.length > 0) {
                          items.push({ type: 'Events', values: uniqueEvents.map(e => e.replace(/_/g, ' ')) });
                        }
                        const objectIds = rule.map(r => r.object_id).filter(Boolean);
                        if (objectIds.length > 0) {
                          items.push({ type: 'Objects', values: [`${objectIds.length} videos/posts`] });
                        }
                        return { items, ruleGroups };
                      }
                      
                      // Handle object with inclusions
                      if (rule.inclusions) {
                        const inc = rule.inclusions;
                        if (inc.operator) items.push({ type: 'Logic', values: [inc.operator.toUpperCase()] });
                        
                        if (inc.rules && Array.isArray(inc.rules)) {
                          inc.rules.forEach((r, rIdx) => {
                            const group = { title: `Rule ${rIdx + 1}`, details: [] };
                            
                            // Event sources
                            if (r.event_sources && Array.isArray(r.event_sources)) {
                              r.event_sources.forEach(src => {
                                if (src.type) group.details.push({ label: 'Source', value: src.type.replace(/_/g, ' ') });
                                if (src.id) group.details.push({ label: 'Pixel ID', value: String(src.id) });
                              });
                            }
                            
                            // Retention at rule level
                            if (r.retention_seconds) {
                              const days = Math.round(r.retention_seconds / 86400);
                              group.details.push({ label: 'Retention', value: `${days} days` });
                            }
                            
                            // Handle nested filter object with filters array
                            if (r.filter && r.filter.filters && Array.isArray(r.filter.filters)) {
                              if (r.filter.operator) {
                                group.details.push({ label: 'Filter Logic', value: r.filter.operator.toUpperCase() });
                              }
                              r.filter.filters.forEach(f => {
                                const field = f.field?.replace(/_/g, ' ') || 'Filter';
                                const op = f.operator || 'eq';
                                const val = f.value || '';
                                const opLabel = op === 'i_contains' ? 'contains' : op === 'eq' ? '=' : op === 'neq' ? '!=' : op === 'i_not_contains' ? 'not contains' : op;
                                group.details.push({ label: field, value: `${opLabel} "${val}"` });
                              });
                            }
                            
                            // Direct filters array (old format)
                            if (r.filters && Array.isArray(r.filters)) {
                              r.filters.forEach(f => {
                                const field = f.field?.replace(/_/g, ' ') || 'Filter';
                                const op = f.operator || 'eq';
                                const val = f.value || '';
                                const opLabel = op === 'i_contains' ? 'contains' : op === 'eq' ? '=' : op === 'neq' ? '!=' : op;
                                group.details.push({ label: field, value: `${opLabel} "${val}"` });
                              });
                            }
                            
                            if (group.details.length > 0) ruleGroups.push(group);
                          });
                        }
                      }
                      
                      // Handle direct rule object (without inclusions wrapper)
                      if (rule.event_sources) {
                        rule.event_sources.forEach(src => {
                          if (src.type) items.push({ type: 'Source', values: [src.type.replace(/_/g, ' ')] });
                          if (src.id) items.push({ type: 'ID', values: [String(src.id)] });
                        });
                      }
                      if (rule.filters) {
                        rule.filters.forEach(f => {
                          const field = f.field?.replace(/_/g, ' ') || 'Filter';
                          const val = f.value || '';
                          items.push({ type: field, values: [val] });
                        });
                      }
                      
                      return { items, ruleGroups };
                    };
                    
                    const { items, ruleGroups } = parseRule(detail.rule);
                    const hasContent = items.length > 0 || ruleGroups.length > 0;
                    
                    return (
                      <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Target className="w-4 h-4 text-slate-600" />
                          <span className="text-sm font-semibold text-slate-800">Audience Rules</span>
                        </div>
                        {hasContent ? (
                          <div className="space-y-3">
                            {/* Summary items */}
                            {items.length > 0 && (
                              <div className="space-y-1.5">
                                {items.map((item, idx) => (
                                  <div key={idx} className="flex items-start gap-2">
                                    <span className="text-xs text-slate-500 min-w-[60px]">{item.type}:</span>
                                    <div className="flex flex-wrap gap-1">
                                      {item.values.map((v, i) => (
                                        <span key={i} className="px-2 py-0.5 rounded text-xs bg-white border border-slate-200 text-slate-700">{v}</span>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            {/* Rule groups */}
                            {ruleGroups.length > 0 && (
                              <div className="space-y-2">
                                {ruleGroups.map((group, gIdx) => (
                                  <div key={gIdx} className="bg-white border border-slate-200 rounded-lg p-3">
                                    <div className="text-xs font-medium text-slate-600 mb-2">{group.title}</div>
                                    <div className="space-y-1">
                                      {group.details.map((d, dIdx) => (
                                        <div key={dIdx} className="flex items-start gap-2 text-xs">
                                          <span className="text-slate-400 min-w-[70px]">{d.label}:</span>
                                          <span className="text-slate-700 font-mono">{d.value}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <pre className="text-xs text-slate-700 bg-white rounded-lg p-3 whitespace-pre-wrap overflow-x-auto max-h-40 font-mono border border-slate-200">
                            {typeof detail.rule === 'string' ? detail.rule : JSON.stringify(detail.rule, null, 2)}
                          </pre>
                        )}
                      </div>
                    );
                  })()}

                  {/* Raw targeting fallback */}
                  {detail.targeting && !detail.targeting_summary && (
                    <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Target className="w-4 h-4 text-slate-600" />
                        <span className="text-sm font-semibold text-slate-800">Targeting Data</span>
                      </div>
                      <pre className="text-xs text-slate-700 bg-white rounded-lg p-3 whitespace-pre-wrap overflow-x-auto max-h-32 font-mono border border-slate-200">
                        {JSON.stringify(detail.targeting, null, 2)}
                      </pre>
                    </div>
                  )}

                  {/* Targeting Summary */}
                  {detail.targeting_summary && (
                    <div className="space-y-3">
                      {/* Interests */}
                      {(detail.targeting_summary.include?.interests || []).length > 0 && (
                        <div className="rounded-lg bg-violet-50 border border-violet-100 p-3">
                          <div className="flex items-center gap-2 mb-2">
                            <Sparkles className="w-4 h-4 text-violet-600" />
                            <span className="text-sm font-medium text-violet-900">Interests</span>
                            <span className="text-xs text-violet-500">({(detail.targeting_summary.include?.interests || []).length})</span>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {(detail.targeting_summary.include?.interests || []).slice(0, 50).map((n, i) => (
                              <span key={`i-${i}`} className="px-2 py-0.5 rounded text-xs bg-white text-violet-700 border border-violet-200">{n}</span>
                            ))}
                            {(detail.targeting_summary.include?.interests || []).length > 50 && (
                              <span className="px-2 py-0.5 rounded text-xs bg-violet-100 text-violet-600">+{(detail.targeting_summary.include?.interests || []).length - 50} more</span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Behaviors */}
                      {(detail.targeting_summary.include?.behaviors || []).length > 0 && (
                        <div className="rounded-lg bg-blue-50 border border-blue-100 p-3">
                          <div className="flex items-center gap-2 mb-2">
                            <Users className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-medium text-blue-900">Behaviors</span>
                            <span className="text-xs text-blue-500">({(detail.targeting_summary.include?.behaviors || []).length})</span>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {(detail.targeting_summary.include?.behaviors || []).slice(0, 30).map((n, i) => (
                              <span key={`b-${i}`} className="px-2 py-0.5 rounded text-xs bg-white text-blue-700 border border-blue-200">{n}</span>
                            ))}
                            {(detail.targeting_summary.include?.behaviors || []).length > 30 && (
                              <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-600">+{(detail.targeting_summary.include?.behaviors || []).length - 30} more</span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Locations & Demographics */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {/* Locations */}
                        {(() => {
                          const allLocs = [
                            ...(detail.targeting_summary.locations?.countries || []),
                            ...(detail.targeting_summary.locations?.countryGroups || []),
                            ...(detail.targeting_summary.locations?.cities || []),
                            ...(detail.targeting_summary.locations?.regions || [])
                          ];
                          return allLocs.length > 0 ? (
                            <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-3">
                              <div className="flex items-center gap-2 mb-2">
                                <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span className="text-sm font-medium text-emerald-900">Locations</span>
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                {allLocs.slice(0, 20).map((n, i) => (
                                  <span key={`loc-${i}`} className="px-2 py-0.5 rounded text-xs bg-white text-emerald-700 border border-emerald-200">{n}</span>
                                ))}
                              </div>
                            </div>
                          ) : null;
                        })()}

                        {/* Demographics */}
                        {(detail.targeting_summary.demographics?.ageMin || detail.targeting_summary.demographics?.ageMax || (detail.targeting_summary.demographics?.genders || []).length > 0) && (
                          <div className="rounded-lg bg-amber-50 border border-amber-100 p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              <span className="text-sm font-medium text-amber-900">Demographics</span>
                            </div>
                            <div className="space-y-1.5">
                              {(detail.targeting_summary.demographics?.ageMin || detail.targeting_summary.demographics?.ageMax) && (
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-amber-600">Age:</span>
                                  <span className="px-2 py-0.5 rounded text-xs bg-white text-amber-700 border border-amber-200 font-medium">
                                    {detail.targeting_summary.demographics?.ageMin || '18'} - {detail.targeting_summary.demographics?.ageMax || '65+'}
                                  </span>
                                </div>
                              )}
                              {(detail.targeting_summary.demographics?.genders || []).length > 0 && (
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-amber-600">Gender:</span>
                                  <div className="flex gap-1">
                                    {(detail.targeting_summary.demographics?.genders || []).map((g, i) => (
                                      <span key={`g-${i}`} className="px-2 py-0.5 rounded text-xs bg-white text-amber-700 border border-amber-200 capitalize">{g}</span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* No targeting data message */}
                  {!detail.targeting_summary && !detail.targeting && !detail.rule && !detail.lookalike && (
                    <div className="text-center py-8 text-gray-500">
                      <Target className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p className="text-sm">No targeting details available for this audience.</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">No details available.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Eski "AI Audience Recommendations" paneli (doğrulamasız GPT önerisi + API'nin izin vermediği saved audience
          oluşturma, Meta #3 hatası) kaldırıldı; yerine Glowy AI · Kitle Önerileri (/meta/kitle-onerileri, şimdilik iç ekip). */}
      {/@(glowytics|heyglowy)\.com$/i.test(String(user?.email || '')) && (
        <Card className="mt-6 rounded-2xl overflow-hidden shadow-sm border border-gray-100">
          <a href="/meta/kitle-onerileri" className="w-full p-4 bg-white flex items-center gap-3 hover:bg-gray-50 transition-colors" style={{ textDecoration: 'none' }}>
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center border border-blue-100">
              <Sparkles className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900">Glowy AI · Audience Suggestions</h2>
              <p className="text-sm text-gray-500">Layered audiences built from account data and verified on Meta. Apply to an ad set or create a new ad set or campaign.</p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </a>
        </Card>
      )}

      {/* Targeting Search Section */}
      <Card className="mt-6 rounded-2xl overflow-hidden shadow-sm border border-gray-100">
        <button
          type="button"
          onClick={() => setShowTargetingSearch(!showTargetingSearch)}
          className="w-full p-4 bg-white text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center border border-indigo-100">
              <Search className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Targeting Explorer</h2>
              <p className="text-sm text-gray-500">Search interests, locations, behaviors, and more from Meta</p>
            </div>
          </div>
          {showTargetingSearch ? (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {showTargetingSearch && (
          <div className="p-4 border-t border-gray-100 bg-gray-50/50">
            {/* Search Controls */}
            <div className="flex flex-wrap gap-3 mb-4">
              {/* Type Selector */}
              <div className="flex flex-wrap gap-1.5">
                {targetingTypes.map(t => {
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => { setTargetingType(t.value); setTargetingResults([]); }}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        targetingType === t.value 
                          ? 'bg-indigo-100 text-indigo-700 border border-indigo-200' 
                          : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {t.label}
                    </button>
                  );
                })}
              </div>

              {/* Search Input */}
              <div className="flex-1 min-w-[200px] flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={targetingSearch}
                    onChange={(e) => setTargetingSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && searchTargeting()}
                    placeholder={`Search ${targetingTypes.find(t => t.value === targetingType)?.label.toLowerCase() || 'targeting'}...`}
                    className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <button
                  type="button"
                  onClick={searchTargeting}
                  disabled={targetingLoading || (!targetingSearch.trim() && targetingType !== 'adlocale')}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {targetingLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  Search
                </button>
              </div>
            </div>

            {/* Error */}
            {targetingError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {targetingError}
              </div>
            )}

            {/* Results */}
            {targetingResults.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">
                    {targetingResults.length} results found
                  </span>
                  <span className="text-xs text-gray-500">
                    Click to copy ID
                  </span>
                </div>
                <div className="divide-y divide-gray-100 max-h-[400px] overflow-y-auto">
                  {targetingResults.map((item, idx) => (
                    <button
                      key={item.id || idx}
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(item.id);
                        // Could show a toast here
                      }}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center justify-between gap-4"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-gray-900 truncate">{item.name}</div>
                        {item.path && (
                          <div className="text-xs text-gray-500 truncate">{item.path.join(' > ')}</div>
                        )}
                        {item.description && (
                          <div className="text-xs text-gray-500 truncate">{item.description}</div>
                        )}
                        {(item.country_code || item.region) && (
                          <div className="text-xs text-gray-500">
                            {[item.country_code, item.region].filter(Boolean).join(', ')}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        {item.audience_size && (
                          <div className="text-right">
                            <div className="text-sm font-semibold text-gray-700">
                              {formatCount(item.audience_size)}
                            </div>
                            {item.audience_size_upper && item.audience_size_upper !== item.audience_size && (
                              <div className="text-xs text-gray-400">
                                up to {formatCount(item.audience_size_upper)}
                              </div>
                            )}
                          </div>
                        )}
                        <div className="text-xs font-mono text-gray-400 bg-gray-100 px-2 py-1 rounded">
                          {item.id}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {!targetingLoading && targetingResults.length === 0 && targetingSearch && (
              <div className="text-center py-8 text-gray-500">
                <Search className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No results. Try a different search term.</p>
              </div>
            )}

            {/* Initial State */}
            {!targetingLoading && targetingResults.length === 0 && !targetingSearch && (
              <div className="text-center py-8 text-gray-500">
                <Heart className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">Enter a search term to explore Meta targeting options.</p>
                <p className="text-xs text-gray-400 mt-1">Try: "jewelry", "fashion", "istanbul", "marketing"</p>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
